// Componente principal de edição de apreciação (refatorado)
import { useEffect, useState, useRef, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-hook';
import { useSidebar } from '@/contexts/sidebar-context';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { TabelaPartes } from '@/components/apreciacao/tabela-partes';
import { ChecklistRevisao } from '@/components/apreciacao/checklist-revisao';
import { PreviewLatex } from '@/components/apreciacao/preview-latex';
import { ModalEditarParte } from '@/components/apreciacao/modal-editar-parte';
import { ModalCriarTemplateParte } from '@/components/apreciacao/modal-criar-template-parte';
import { ModalSelecionarTemplateParte } from '@/components/apreciacao/modal-selecionar-template-parte';
import { ModalCriarTemplateMultiParte } from '@/components/apreciacao/modal-criar-template-multiparte';
import { ModalSelecionarTemplateMultiParte } from '@/components/apreciacao/modal-selecionar-template-multiparte';
import { NavegacaoEdicaoMaquina } from '@/components/apreciacao/navegacao-edicao-maquina';
import { CabecalhoIdentificacao } from '@/components/apreciacao/cabecalho-identificacao';
import { Etapa1DadosMaquina } from '@/components/apreciacao/etapa1-dados-maquina';
import { ResizableDivider } from '@/components/apreciacao/resizable-divider';
import { FotosCampoGaleria } from '@/components/apreciacao/fotos-campo-galeria';
import { apiService, UploadedFoto, EncaixeFotosResultado } from '@/services/api';
import { Sparkles, Loader2, X, Check } from 'lucide-react';
import { toAbsoluteFotoUrl, withPreviewParams } from '@/utils/foto-url';
import { useApreciacaoLock } from './hooks/useApreciacaoLock';
import { useApreciacaoData } from './hooks/useApreciacaoData';
import { useApreciacaoSave } from './hooks/useApreciacaoSave';
import { useApreciacaoPartes } from './hooks/useApreciacaoPartes';
import { LockedScreen } from './components/LockedScreen';
import { LoadingScreen } from './components/LoadingScreen';
import { EditHeader } from './components/EditHeader';
import { BannerProvenienciaIA } from '@/features/apreciacao-ia/BannerProvenienciaIA';

export function ApreciacaoEdit() {
  const { t, formatarDataHora } = useI18n();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { setSidebarCollapsed } = useSidebar();
  const { projeto } = useProjeto();
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');
  const [etapaAtiva, setEtapaAtiva] = useState<'dados' | 'partes'>('dados');
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [modalCriarTemplateAberto, setModalCriarTemplateAberto] = useState(false);
  const [modalSelecionarTemplateAberto, setModalSelecionarTemplateAberto] = useState(false);
  const [modalCriarTemplateMultiParteAberto, setModalCriarTemplateMultiParteAberto] = useState(false);
  const [modalSelecionarTemplateMultiParteAberto, setModalSelecionarTemplateMultiParteAberto] = useState(false);
  const [parteParaTemplate, setParteParaTemplate] = useState<{ parte: string; riscos: any[] } | null>(null);
  const [modalImportacaoJsonAberto, setModalImportacaoJsonAberto] = useState(false);
  const [jsonImportacao, setJsonImportacao] = useState('');
  const [jsonParteNome, setJsonParteNome] = useState('');
  const [jsonFotoParte1, setJsonFotoParte1] = useState<File | null>(null);
  const [jsonFotoParte2, setJsonFotoParte2] = useState<File | null>(null);
  const [jsonFotoParte3, setJsonFotoParte3] = useState<File | null>(null);
  const [jsonFotoParte4, setJsonFotoParte4] = useState<File | null>(null);
  const [importandoJson, setImportandoJson] = useState(false);
  const [fotosVinculadas, setFotosVinculadas] = useState<UploadedFoto[]>([]);
  const [pictogramas, setPictogramas] = useState<UploadedFoto[]>([]);
  const [painelDireito, setPainelDireito] = useState<'preview' | 'fotos'>('preview');
  const [encaixando, setEncaixando] = useState(false);
  const [encaixeResultado, setEncaixeResultado] = useState<EncaixeFotosResultado | null>(null);
  const [encaixeErro, setEncaixeErro] = useState<string | null>(null);
  const [aplicandoParte, setAplicandoParte] = useState<string | null>(null);
  // Visibilidade do modal separada do resultado: fechar NÃO descarta a análise.
  const [mostrarEncaixe, setMostrarEncaixe] = useState(false);
  const [encaixeGeradoEm, setEncaixeGeradoEm] = useState<string | null>(null);

  // Hook para gerenciar dados (precisa vir primeiro para limparDados estar disponível)
  const {
    apreciacao,
    dados,
    riscos,
    loading: dataLoading,
    setDados,
    setRiscos,
    carregarApreciacao,
    limparDados,
  } = useApreciacaoData();

  // URLs de fotos de campo (/uploads/fotos/...) já usadas em alguma parte.
  // Usado para marcar e jogar essas fotos para o final da galeria.
  const fotosUsadasUrls = useMemo(() => {
    const urls: string[] = [];
    for (const r of riscos || []) {
      for (const k of ['fotoAtualUrl1', 'fotoAtualUrl2', 'fotoAtualUrl3', 'fotoAtualUrl4', 'fotoPlrUrl'] as const) {
        const v = (r as any)[k];
        if (typeof v === 'string' && v.includes('/uploads/fotos/')) urls.push(v);
      }
    }
    return urls;
  }, [riscos]);

  const handleEncaixarFotos = async () => {
    if (!id || encaixando) return;
    setEncaixando(true);
    setEncaixeErro(null);
    setEncaixeResultado(null);
    try {
      const resp = await apiService.encaixarFotosComIA(id);
      if (resp.success && resp.data) {
        setEncaixeResultado(resp.data);
        setEncaixeGeradoEm(new Date().toISOString());
        setMostrarEncaixe(true);
        // Recarrega as fotos para refletir as descrições da IA recém-salvas
        // (indicativo "Analisada" + descrição no hover da galeria).
        try {
          const rf = await apiService.listarFotosPorApreciacao(id);
          if (rf.success && rf.data?.fotos) setFotosVinculadas(rf.data.fotos);
        } catch {
          /* mantém as fotos atuais */
        }
      } else {
        setEncaixeErro(resp.message || t('apreciacoes.editar.encaixe.falha'));
      }
    } catch (e) {
      setEncaixeErro(e instanceof Error ? e.message : t('apreciacoes.editar.encaixe.erro'));
    } finally {
      setEncaixando(false);
    }
  };

  // Aplica a sugestão de uma parte: define as fotos (se houver) e os textos
  // sugeridos (apenas os não vazios) em todos os riscos daquela parte.
  const aplicarEncaixeParte = async (sugestao: EncaixeFotosResultado['partes'][number]) => {
    if (!id) return;
    const riscosDaParte = (riscos || []).filter((r: any) => (r.parte || '').trim() === sugestao.parte.trim() && r.id);
    if (riscosDaParte.length === 0) {
      setEncaixeErro(t('apreciacoes.editar.encaixe.semRiscoParte', { parte: sugestao.parte }));
      return;
    }
    setAplicandoParte(sugestao.parte);
    setEncaixeErro(null);
    try {
      const dados: any = {};
      if (sugestao.fotos.length > 0) {
        dados.fotoAtualUrl1 = sugestao.fotos[0]?.url ?? null;
        dados.fotoAtualUrl2 = sugestao.fotos[1]?.url ?? null;
        dados.fotoAtualUrl3 = sugestao.fotos[2]?.url ?? null;
        dados.fotoAtualUrl4 = sugestao.fotos[3]?.url ?? null;
      }
      if (sugestao.sugestoes.descricaoCondicaoAtual) dados.adequacoesTecnicas = sugestao.sugestoes.descricaoCondicaoAtual;
      if (sugestao.sugestoes.perigosEncontrados) dados.perigosEncontrados = sugestao.sugestoes.perigosEncontrados;
      if (sugestao.sugestoes.consequencias) dados.consequencias = sugestao.sugestoes.consequencias;
      if (sugestao.sugestoes.eventoPerigoso) dados.eventoPerigoso = sugestao.sugestoes.eventoPerigoso;

      for (const r of riscosDaParte) {
        await apiService.atualizarRisco(r.id, dados);
      }
      await carregarApreciacao(id);
    } catch (e) {
      setEncaixeErro(e instanceof Error ? e.message : t('apreciacoes.editar.encaixe.erroAplicar'));
    } finally {
      setAplicandoParte(null);
    }
  };

  // Hook para gerenciar locks
  const {
    lockInfo,
    lockLoading,
    lockInicializado,
    erroLock,
    removerLock,
    renovar,
    eMeuLock,
    bloqueadoPorOutro,
    inicializarLock,
  } = useApreciacaoLock({
    apreciacaoId: id,
    usuarioId: user?.id || '',
    onLockBlocked: () => {
      // Limpar dados quando bloqueado
      limparDados();
    },
  });

  // Hook para gerenciar salvamento
  const { saving, salvar } = useApreciacaoSave({
    apreciacaoId: id,
    dados,
    riscos,
    renovarLock: renovar,
    bloqueadoPorOutro,
    erroLock,
    onSalvo: async () => {
      if (id) {
        await carregarApreciacao(id);
      }
    },
  });

  // Hook para gerenciar partes
  const {
    modalParteAberto,
    parteSelecionada,
    handleEditarParte,
    handleFecharModal,
    handleDeletarParte,
    handleDuplicarParte,
    handleAdicionarParte,
    handleReordenarPartes,
  } = useApreciacaoPartes({
    apreciacaoId: id,
    riscos,
    setRiscos,
  });

  // Página estimada da última parte editada (para navegação no preview)
  // Seções fixas A, B, C ocupam ~13 páginas; cada parte ocupa ~3 páginas
  const [paginaUltimaParte, setPaginaUltimaParte] = useState<number>(14);

  /** Pasta de imagens vinculada — usada para localizar o job de IA que gerou esta apreciação. */
  const [pastaVinculada, setPastaVinculada] = useState<string | null>(null);

  // Carregar fotos de campo vinculadas a esta apreciação (pasta de imagens).
  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    apiService
      .listarFotosPorApreciacao(id)
      .then((r) => {
        if (cancelado) return;
        setFotosVinculadas(r.success && r.data?.fotos ? r.data.fotos : []);
        setPastaVinculada(r.success && r.data?.machineFolder ? r.data.machineFolder : null);
      })
      .catch(() => {
        if (!cancelado) {
          setFotosVinculadas([]);
          setPastaVinculada(null);
        }
      });
    return () => {
      cancelado = true;
    };
  }, [id]);

  // Carrega a última análise de encaixe salva (se houver) para poder reabrir
  // sem reprocessar. Não abre o modal automaticamente.
  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    apiService
      .obterEncaixeSalvo(id)
      .then((r) => {
        if (cancelado || !r.success || !r.data) return;
        setEncaixeResultado(r.data.resultado);
        setEncaixeGeradoEm(r.data.geradoEm);
      })
      .catch(() => {
        /* sem análise salva */
      });
    return () => {
      cancelado = true;
    };
  }, [id]);

  // Pictogramas/plaquetas padrão (independem da apreciação; carrega uma vez)
  useEffect(() => {
    let cancelado = false;
    apiService
      .listarPictogramas()
      .then((r) => {
        if (cancelado) return;
        setPictogramas(r.success && Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelado) setPictogramas([]);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  // Efeito para gerenciar sidebar
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
      if (id) {
        removerLock().catch((err) => {
          console.warn('Erro ao remover lock no cleanup:', err);
        });
      }
    };
  }, [setSidebarCollapsed, id, removerLock]);

  // Efeito para inicializar lock e carregar dados (apenas uma vez por ID)
  const inicializadoRef = useRef<string | null>(null);
  useEffect(() => {
    if (id && user?.id && inicializadoRef.current !== id) {
      inicializadoRef.current = id;
      const inicializar = async () => {
        const lockOk = await inicializarLock();
        if (lockOk) {
          await carregarApreciacao(id);
        }
      };
      inicializar();
    }

    // Reset quando o ID mudar
    return () => {
      if (id && inicializadoRef.current === id) {
        inicializadoRef.current = null;
      }
    };
  }, [id, user?.id]); // Apenas ID e user?.id como dependências

  // Handlers
  const handleChange = (campo: string, valor: any) => {
    if (campo === 'maquina') {
      setDados((prev: any) => ({
        ...prev,
        maquina: valor,
      }));
    } else if (campo === 'ativo') {
      setDados((prev: any) => ({
        ...prev,
        ativo: valor,
        ativoId: valor?.id || null,
      }));
    } else {
      setDados((prev: any) => ({
        ...prev,
        [campo]: valor,
      }));
    }

    // Renovar lock a cada modificação
    if (eMeuLock()) {
      renovar();
    }
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    await salvar();
  };

  const handleAbrirImportacaoJson = () => {
    setModalImportacaoJsonAberto(true);
  };

  const handleFecharImportacaoJson = () => {
    if (importandoJson) return;
    setModalImportacaoJsonAberto(false);
    setJsonImportacao('');
    setJsonParteNome('');
    setJsonFotoParte1(null);
    setJsonFotoParte2(null);
    setJsonFotoParte3(null);
    setJsonFotoParte4(null);
  };

  const handleImportarParteViaJson = async () => {
    if (!id) {
      alert(t('apreciacoes.editar.json.semId'));
      return;
    }

    if (!jsonImportacao.trim()) {
      alert(t('apreciacoes.editar.json.semJson'));
      return;
    }

    try {
      setImportandoJson(true);
      const parsed = JSON.parse(jsonImportacao);

      // Formatos aceitos:
      // 1) { "parte": "...", "riscos": [ ... ] }
      // 2) [ ...riscos ]
      // 3) { ...risco }
      const riscosJson = Array.isArray(parsed)
        ? parsed
        : (Array.isArray(parsed?.riscos) ? parsed.riscos : [parsed]);

      if (!Array.isArray(riscosJson) || riscosJson.length === 0) {
        alert(t('apreciacoes.editar.json.semRiscos'));
        return;
      }

      const parteJson = typeof parsed?.parte === 'string' ? parsed.parte : '';
      const partesUnicas = new Set(riscos.map((r: any) => r.parte || ''));
      const proximoNumeroParte = partesUnicas.size + 1;

      let parteFinal = jsonParteNome.trim() || parteJson.trim() || `Parte ${proximoNumeroParte}`;
      if (!jsonParteNome.trim() && !parteJson.trim()) {
        let contador = 1;
        while (partesUnicas.has(parteFinal)) {
          contador++;
          parteFinal = `Parte ${proximoNumeroParte} (${contador})`;
        }
      }

      const maxNumeroAtual = riscos.length > 0
        ? Math.max(...riscos.map((r: any) => r.numero || 0))
        : 0;
      let proximoNumeroRisco = maxNumeroAtual + 1;

      // Upload das imagens da parte (se houver)
      const [fotoParte1Url, fotoParte2Url, fotoParte3Url, fotoParte4Url] = await Promise.all([
        jsonFotoParte1 ? apiService.uploadFotoParte(id, jsonFotoParte1) : Promise.resolve(null),
        jsonFotoParte2 ? apiService.uploadFotoParte(id, jsonFotoParte2) : Promise.resolve(null),
        jsonFotoParte3 ? apiService.uploadFotoParte(id, jsonFotoParte3) : Promise.resolve(null),
        jsonFotoParte4 ? apiService.uploadFotoParte(id, jsonFotoParte4) : Promise.resolve(null),
      ]);

      if (fotoParte1Url && !fotoParte1Url.success)
        throw new Error(fotoParte1Url.message || t('apreciacoes.editar.json.erroImagem', { indice: 1 }));
      if (fotoParte2Url && !fotoParte2Url.success)
        throw new Error(fotoParte2Url.message || t('apreciacoes.editar.json.erroImagem', { indice: 2 }));
      if (fotoParte3Url && !fotoParte3Url.success)
        throw new Error(fotoParte3Url.message || t('apreciacoes.editar.json.erroImagem', { indice: 3 }));
      if (fotoParte4Url && !fotoParte4Url.success)
        throw new Error(fotoParte4Url.message || t('apreciacoes.editar.json.erroImagem', { indice: 4 }));

      const riscosCriados: any[] = [];
      for (const riscoRaw of riscosJson) {
        const risco = typeof riscoRaw === 'object' && riscoRaw !== null ? riscoRaw : {};
        const numeroRisco = Number.isFinite(Number(risco.numero)) && Number(risco.numero) > 0
          ? Number(risco.numero)
          : proximoNumeroRisco++;

        const payload: any = {
          ...risco,
          numero: numeroRisco,
          parte: risco.parte || parteFinal,
          tipoPerigo: risco.tipoPerigo || 'mecanico',
          categoria: risco.categoria || '',
          gravidadeAtual: risco.gravidadeAtual ?? 1,
          frequenciaAtual: risco.frequenciaAtual ?? 1,
          exposicaoAtual: risco.exposicaoAtual ?? 1,
          probabilidadeAtual: risco.probabilidadeAtual ?? 1,
          cicloVida: risco.cicloVida || '',
          eventoPerigoso: risco.eventoPerigoso || '',
          consequencias: risco.consequencias || '',
          fotoAtualUrl1: risco.fotoAtualUrl1 ?? (fotoParte1Url?.data?.url || undefined),
          fotoAtualUrl2: risco.fotoAtualUrl2 ?? (fotoParte2Url?.data?.url || undefined),
          fotoAtualUrl3: risco.fotoAtualUrl3 ?? (fotoParte3Url?.data?.url || undefined),
          fotoAtualUrl4: risco.fotoAtualUrl4 ?? (fotoParte4Url?.data?.url || undefined),
        };

        // Campos que não devem vir no POST de criação de risco
        delete payload.id;
        delete payload.apreciacaoId;
        delete payload.createdAt;
        delete payload.updatedAt;

        const response = await apiService.adicionarRisco(id, payload);
        if (!response.success || !response.data) {
          throw new Error(
            response.message || t('apreciacoes.editar.json.erroRisco', { numero: numeroRisco })
          );
        }
        riscosCriados.push(response.data);
      }

      setRiscos([...riscos, ...riscosCriados]);
      handleFecharImportacaoJson();
      alert(
        t('apreciacoes.editar.json.concluida', {
          total: riscosCriados.length,
          parte: parteFinal,
        })
      );
    } catch (error: any) {
      console.error('Erro ao importar JSON da parte:', error);
      alert(
        t('apreciacoes.editar.json.erroImportar', {
          detalhe: error?.message || t('apreciacoes.editar.json.jsonInvalido'),
        })
      );
    } finally {
      setImportandoJson(false);
    }
  };

  const handleSalvarParte = async (dadosParte: any) => {
    if (!id || !parteSelecionada) return;

    try {
      const novoNomeParte = dadosParte.parte || parteSelecionada.parte;
      const parteAntiga = dadosParte.parteAntiga || parteSelecionada.parte;

      // Se é uma parte nova (sem riscos), usar os riscos do modal
      const riscosParaProcessar = parteSelecionada.riscos.length > 0
        ? parteSelecionada.riscos
        : dadosParte.riscos || [];

      // Atualizar cada risco da parte no backend
      for (const risco of riscosParaProcessar) {
        const riscoAtualizado = dadosParte.riscos?.find((r: any) =>
          (r.id && r.id === risco.id) || (r.numero && r.numero === risco.numero)
        ) || risco;

        // tipoPerigo deve conter APENAS os tipos principais (Mecânico, Elétrico, Ergonômico)
        const tiposPerigoUnicos = new Set<string>();

        // Adicionar apenas tipos principais
        if (dadosParte.tiposPerigo && Array.isArray(dadosParte.tiposPerigo)) {
          dadosParte.tiposPerigo.forEach((tp: string) => {
            if (tp && tp.trim()) {
              tiposPerigoUnicos.add(tp.trim());
            }
          });
        }

        const tiposPerigoFinal = Array.from(tiposPerigoUnicos);

        // Perigos encontrados serão salvos no campo perigosEncontrados (separado por vírgula)
        const perigosEncontradosParaSalvar = dadosParte.perigosEncontrados && Array.isArray(dadosParte.perigosEncontrados)
          ? dadosParte.perigosEncontrados.filter((pe: string) => pe && pe.trim() && pe.trim() !== 'teste').join(', ')
          : undefined;

        const dadosAtualizacao: any = {
          parte: novoNomeParte,
          fotoAtualUrl1: dadosParte.fotoParte1 !== undefined ? (dadosParte.fotoParte1 === '' ? null : dadosParte.fotoParte1) : undefined,
          fotoAtualUrl2: dadosParte.fotoParte2 !== undefined ? (dadosParte.fotoParte2 === '' ? null : dadosParte.fotoParte2) : undefined,
          fotoAtualUrl3: dadosParte.fotoParte3 !== undefined ? (dadosParte.fotoParte3 === '' ? null : dadosParte.fotoParte3) : undefined,
          fotoAtualUrl4: dadosParte.fotoParte4 !== undefined ? (dadosParte.fotoParte4 === '' ? null : dadosParte.fotoParte4) : undefined,
          cicloVida: (dadosParte.ciclosVida && dadosParte.ciclosVida.length > 0) ? dadosParte.ciclosVida.join(', ') : null,
          eventoPerigoso: dadosParte.eventoPerigoso !== undefined ? (dadosParte.eventoPerigoso === '' ? null : dadosParte.eventoPerigoso) : undefined,
          conformeAtual: dadosParte.conforme,
          tipoPerigo: tiposPerigoFinal.length > 0 ? tiposPerigoFinal.join(', ') : null,
          perigosEncontrados: perigosEncontradosParaSalvar || null,
          consequencias: dadosParte.potenciaisConsequencias.length > 0 ? dadosParte.potenciaisConsequencias.join(', ') : null,
          potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas && dadosParte.potenciaisConsequenciasEspecificas.length > 0
            ? dadosParte.potenciaisConsequenciasEspecificas
            : null,
          origemEspecifica: dadosParte.origemEspecifica && dadosParte.origemEspecifica.length > 0
            ? dadosParte.origemEspecifica
            : null,
          pronta: dadosParte.pronta,
          probabilidadeAtual: riscoAtualizado.probabilidadeAtual || undefined,
          frequenciaAtual: riscoAtualizado.frequenciaAtual || undefined,
          gravidadeAtual: riscoAtualizado.gravidadeAtual || undefined,
          exposicaoAtual: riscoAtualizado.exposicaoAtual || undefined,
          hrnAtual: riscoAtualizado.hrnAtual || undefined,
          // Valores futuros - sempre enviar se existirem, mesmo que sejam iguais aos atuais
          probabilidadeFutura: riscoAtualizado.probabilidadeFuturo !== undefined && riscoAtualizado.probabilidadeFuturo !== null
            ? Number(riscoAtualizado.probabilidadeFuturo)
            : undefined,
          frequenciaFutura: riscoAtualizado.frequenciaFuturo !== undefined && riscoAtualizado.frequenciaFuturo !== null
            ? Number(riscoAtualizado.frequenciaFuturo)
            : undefined,
          gravidadeFutura: riscoAtualizado.gravidadeFuturo !== undefined && riscoAtualizado.gravidadeFuturo !== null
            ? Number(riscoAtualizado.gravidadeFuturo)
            : undefined,
          exposicaoFutura: riscoAtualizado.exposicaoFuturo !== undefined && riscoAtualizado.exposicaoFuturo !== null
            ? Number(riscoAtualizado.exposicaoFuturo)
            : undefined,
          hrnFuturo: riscoAtualizado.hrnFuturo !== undefined && riscoAtualizado.hrnFuturo !== null
            ? Number(riscoAtualizado.hrnFuturo)
            : undefined,
          adequacoesTecnicas: dadosParte.descricaoCondicaoAtual.length > 0 ? dadosParte.descricaoCondicaoAtual.join('\n') : null,
          medidasControle: dadosParte.medidasControle.length > 0 ? dadosParte.medidasControle.join('\n') : null,
          exigenciasNormativas: dadosParte.exigenciasNormativas.length > 0 ? dadosParte.exigenciasNormativas.join('\n') : null,
          adequacoesNormativas: dadosParte.adequacoesNormativas.length > 0 ? dadosParte.adequacoesNormativas.join('\n') : null,
          // Campos SRP/CS (Sistema de Comando Relacionado à Segurança)
          severidadeSRPCS: riscoAtualizado.severidadeSRPCS || null,
          frequenciaSRPCS: riscoAtualizado.frequenciaSRPCS || null,
          possibilidadeSRPCS: riscoAtualizado.possibilidadeSRPCS || null,
          categoriaSeguranca: riscoAtualizado.categoriaSeguranca || null,
          plr: riscoAtualizado.plr || null,
          sil: riscoAtualizado.sil || null,
          // Campos de exibição no LaTeX
          exibirHRN: dadosParte.exibirHRN !== undefined ? dadosParte.exibirHRN : riscoAtualizado.exibirHRN,
          exibirSRPCS: dadosParte.exibirSRPCS !== undefined ? dadosParte.exibirSRPCS : riscoAtualizado.exibirSRPCS,
        };

        // Remover campos undefined, mas manter campos futuros se foram definidos
        // O backend precisa receber os valores futuros para recalcular o HRN
        Object.keys(dadosAtualizacao).forEach(key => {
          // Não remover campos futuros se eles foram definidos (mesmo que sejam iguais aos atuais)
          const camposFuturos = ['probabilidadeFutura', 'frequenciaFutura', 'gravidadeFutura', 'exposicaoFutura', 'hrnFuturo'];
          if (camposFuturos.includes(key)) {
            // Manter esses campos se foram definidos (não undefined e não null)
            if (dadosAtualizacao[key] !== undefined && dadosAtualizacao[key] !== null) {
              return; // Manter o campo
            } else {
              delete dadosAtualizacao[key]; // Remover se for undefined ou null
            }
          } else {
            // Para outros campos, remover se undefined
            if (dadosAtualizacao[key] === undefined) {
              delete dadosAtualizacao[key];
            }
          }
        });

        if (risco.id) {
          const response = await apiService.atualizarRisco(risco.id, dadosAtualizacao);
          if (!response.success) {
            console.error(`Erro ao atualizar risco ${risco.id}:`, response.message);
            throw new Error(response.message || t('apreciacoes.salvar.partes.erroAtualizarRisco'));
          }
        } else {
          const response = await apiService.adicionarRisco(id, {
            ...risco,
            ...dadosAtualizacao,
            numero: risco.numero || 1,
          });
          if (!response.success) {
            console.error('Erro ao adicionar risco:', response.message);
            throw new Error(response.message || t('apreciacoes.salvar.partes.erroAdicionarRisco'));
          }
        }
      }

      // Atualizar estado local
      // Se é uma parte nova, adicionar os novos riscos
      if (parteSelecionada.riscos.length === 0) {
        // Recarregar dados para obter IDs atualizados
        const response = await apiService.buscarApreciacao(id);
        if (response.success && response.data) {
          let riscosMapeados = (response.data.riscos || []).map((risco: any) => ({
            ...risco,
            probabilidadeFuturo: risco.probabilidadeFutura,
            frequenciaFuturo: risco.frequenciaFutura,
            gravidadeFuturo: risco.gravidadeFutura,
            exposicaoFuturo: risco.exposicaoFutura,
          }));
          const imagensRes = await apiService.buscarRiscosImagens(id!);
          if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
            riscosMapeados = riscosMapeados.map((r: any) => {
              const imgs = imagensRes.data![r.id];
              if (!imgs) return r;
              return {
                ...r,
                fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
                fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
                fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
                fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
                fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
              };
            });
          }
          setRiscos(riscosMapeados);
        }

        handleFecharModal();
        return;
      }

      const novosRiscos = riscos.map(risco => {
        if (risco.parte === parteAntiga) {
          const riscoAtualizado = dadosParte.riscos.find((r: any) =>
            (r.id && r.id === risco.id) || (r.numero && r.numero === risco.numero)
          ) || risco;

          // tipoPerigo deve conter APENAS os tipos principais
          const tiposPerigoUnicosLocal = new Set<string>();

          if (dadosParte.tiposPerigo && Array.isArray(dadosParte.tiposPerigo)) {
            dadosParte.tiposPerigo.forEach((tp: string) => {
              if (tp && tp.trim()) {
                tiposPerigoUnicosLocal.add(tp.trim());
              }
            });
          }

          const tiposPerigoFinal = Array.from(tiposPerigoUnicosLocal);

          // Perigos encontrados serão salvos no campo perigosEncontrados
          const perigosEncontradosParaSalvar = dadosParte.perigosEncontrados && Array.isArray(dadosParte.perigosEncontrados)
            ? dadosParte.perigosEncontrados.filter((pe: string) => pe && pe.trim() && pe.trim() !== 'teste').join(', ')
            : risco.perigosEncontrados || 'teste';

          return {
            ...risco,
            ...riscoAtualizado,
            parte: novoNomeParte,
            fotoAtualUrl1: dadosParte.fotoParte1,
            fotoAtualUrl2: dadosParte.fotoParte2,
            fotoAtualUrl3: dadosParte.fotoParte3,
            fotoAtualUrl4: dadosParte.fotoParte4,
            cicloVida: dadosParte.ciclosVida.join(', '),
            eventoPerigoso: dadosParte.eventoPerigoso,
            conformeAtual: dadosParte.conforme,
            tipoPerigo: tiposPerigoFinal.length > 0 ? tiposPerigoFinal.join(', ') : risco.tipoPerigo,
            perigosEncontrados: perigosEncontradosParaSalvar,
            consequencias: dadosParte.potenciaisConsequencias.join(', '),
            potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas && dadosParte.potenciaisConsequenciasEspecificas.length > 0
              ? dadosParte.potenciaisConsequenciasEspecificas
              : undefined,
            origemEspecifica: dadosParte.origemEspecifica && dadosParte.origemEspecifica.length > 0
              ? dadosParte.origemEspecifica
              : undefined,
            pronta: dadosParte.pronta,
            probabilidadeAtual: riscoAtualizado.probabilidadeAtual || risco.probabilidadeAtual,
            frequenciaAtual: riscoAtualizado.frequenciaAtual || risco.frequenciaAtual,
            gravidadeAtual: riscoAtualizado.gravidadeAtual || risco.gravidadeAtual,
            exposicaoAtual: riscoAtualizado.exposicaoAtual || risco.exposicaoAtual,
            hrnAtual: riscoAtualizado.hrnAtual || risco.hrnAtual,
            probabilidadeFuturo: riscoAtualizado.probabilidadeFuturo || risco.probabilidadeFuturo,
            frequenciaFuturo: riscoAtualizado.frequenciaFuturo || risco.frequenciaFuturo,
            gravidadeFuturo: riscoAtualizado.gravidadeFuturo || risco.gravidadeFuturo,
            exposicaoFuturo: riscoAtualizado.exposicaoFuturo || risco.exposicaoFuturo,
            hrnFuturo: riscoAtualizado.hrnFuturo || risco.hrnFuturo,
            // Campos SRP/CS (Sistema de Comando Relacionado à Segurança)
            severidadeSRPCS: riscoAtualizado.severidadeSRPCS || risco.severidadeSRPCS,
            frequenciaSRPCS: riscoAtualizado.frequenciaSRPCS || risco.frequenciaSRPCS,
            possibilidadeSRPCS: riscoAtualizado.possibilidadeSRPCS || risco.possibilidadeSRPCS,
            categoriaSeguranca: riscoAtualizado.categoriaSeguranca || risco.categoriaSeguranca,
            plr: riscoAtualizado.plr || risco.plr,
            sil: riscoAtualizado.sil || risco.sil,
          };
        }
        return risco;
      });

      setRiscos(novosRiscos);

      const response = await apiService.buscarApreciacao(id);
      if (response.success && response.data) {
        let riscosMapeados = (response.data.riscos || []).map((risco: any) => ({
          ...risco,
          probabilidadeFuturo: risco.probabilidadeFutura,
          frequenciaFuturo: risco.frequenciaFutura,
          gravidadeFuturo: risco.gravidadeFutura,
          exposicaoFuturo: risco.exposicaoFutura,
        }));
        const imagensRes = await apiService.buscarRiscosImagens(id!);
        if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
          riscosMapeados = riscosMapeados.map((r: any) => {
            const imgs = imagensRes.data![r.id];
            if (!imgs) return r;
            return {
              ...r,
              fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
              fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
              fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
              fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
              fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
            };
          });
        }
        setRiscos(riscosMapeados);
      }

      handleFecharModal();
    } catch (error) {
      console.error('Erro ao salvar parte:', error);
      throw error;
    }
  };

  const handleCriarTemplate = (parte: string, riscosParte: any[]) => {
    setParteParaTemplate({ parte, riscos: riscosParte });
    setModalCriarTemplateAberto(true);
  };

  const handleFecharModalCriarTemplate = () => {
    setModalCriarTemplateAberto(false);
    setParteParaTemplate(null);
  };

  const handleSucessoCriarTemplate = async () => {
    // Recarregar dados se necessário
    if (id) {
      await carregarApreciacao(id);
    }
  };

  const handleFecharModalSelecionarTemplate = () => {
    setModalSelecionarTemplateAberto(false);
  };

  const handleSucessoAplicarTemplate = async () => {
    // Recarregar dados para mostrar os novos riscos
    if (id) {
      await carregarApreciacao(id);
    }
  };

  const handleCriarTemplateMultiParte = () => {
    setModalCriarTemplateMultiParteAberto(true);
  };

  const handleFecharModalCriarTemplateMultiParte = () => {
    setModalCriarTemplateMultiParteAberto(false);
  };

  const handleSucessoCriarTemplateMultiParte = async () => {
    if (id) {
      await carregarApreciacao(id);
    }
  };

  const handleAdicionarTemplateMultiParte = () => {
    setModalSelecionarTemplateMultiParteAberto(true);
  };

  const handleFecharModalSelecionarTemplateMultiParte = () => {
    setModalSelecionarTemplateMultiParteAberto(false);
  };

  const handleSucessoAplicarTemplateMultiParte = async () => {
    if (id) {
      await carregarApreciacao(id);
    }
  };

  // Estados de loading
  const loading = dataLoading || lockLoading || !lockInicializado;

  // Tela de bloqueio
  if (erroLock) {
    return <LockedScreen erroLock={erroLock} apreciacaoId={id} />;
  }

  // Tela de carregamento
  if (loading) {
    return (
      <LoadingScreen
        mensagem={
          lockLoading
            ? t('apreciacoes.editar.verificandoBloqueio')
            : t('apreciacoes.editar.carregandoApreciacao')
        }
      />
    );
  }

  if (!dados) {
    return <LoadingScreen mensagem={t('apreciacoes.editar.carregandoDados')} />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-6 bg-white dark:bg-schmersal-gray-darker border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
        <EditHeader
          titulo={dados.titulo || t('apreciacoes.editar.tituloFallback', { numero: dados.numero || '' })}
          numero={dados.numero}
          lockInfo={lockInfo}
          eMeuLock={eMeuLock()}
          bloqueadoPorOutro={bloqueadoPorOutro()}
          onSave={(e) => handleSalvar(e || { preventDefault: () => { } } as React.FormEvent)}
          saving={saving}
          disabled={bloqueadoPorOutro() && !eMeuLock()}
        />
      </div>

      {/* Aviso se bloqueado por outro usuário (só mostrar se NÃO for meu lock) */}
      {bloqueadoPorOutro() && !eMeuLock() && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <strong>{t('apreciacoes.aviso.atencao')}</strong> {t('apreciacoes.editar.aviso.bloqueadaLeitura')}
          </p>
        </div>
      )}

      {/* Proveniência da geração por IA: como não há tela de revisão, é aqui que
          o técnico vê o que foi inferido e o que precisa conferir. */}
      {id && pastaVinculada && projeto?.id && (
        <div className="flex-shrink-0 px-6 pt-4">
          <BannerProvenienciaIA
            apreciacaoId={id}
            machineFolder={pastaVinculada}
            projetoId={projeto.id}
          />
        </div>
      )}

      {/* Navegação de Etapas - Sempre acima */}
      <div className="flex-shrink-0">
        <NavegacaoEdicaoMaquina
          etapaAtiva={etapaAtiva}
          onEtapaChange={setEtapaAtiva}
        />
      </div>

      {/* Mobile View Toggle */}
      <div className="lg:hidden flex border-b border-gray-200 mx-6">
        <button
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 ${activeTab === 'form'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => setActiveTab('form')}
        >
          {t('apreciacoes.editar.tab.edicao')}
        </button>
        <button
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 ${activeTab === 'preview'
            ? 'border-blue-500 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => setActiveTab('preview')}
        >
          {t('apreciacoes.editar.tab.preview')}
        </button>
      </div>

      {/* Layout: Formulário e Preview */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Formulário à esquerda */}
        <div
          className={`painel-redimensionavel flex-shrink-0 overflow-y-auto ${activeTab === 'preview' ? 'hidden lg:block' : ''}`}
          style={{ ['--painel-largura' as string]: `${leftPanelWidth}%` } as CSSProperties}
        >
          <div className="p-6">
            <form onSubmit={handleSalvar} className="space-y-6">
              {etapaAtiva === 'dados' ? (
                <>
                  <CabecalhoIdentificacao
                    dados={dados}
                    onChange={handleChange}
                    modoEdicao={true}
                  />
                  <Etapa1DadosMaquina
                    dados={dados}
                    onChange={handleChange}
                    modoEdicao={true}
                    apreciacaoId={id}
                  />
                </>
              ) : (
                <div className="space-y-4">
                  {/* Comentários do cliente na frente das partes: é aqui que a
                      adequação é feita, e perder um item custa outra rodada. */}
                  {id && <ChecklistRevisao apreciacaoId={id} />}

                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('apreciacoes.editar.templates.titulo')}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCriarTemplateMultiParte}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                      >
                        {t('apreciacoes.editar.templates.criarCompleto')}
                      </button>
                      <button
                        type="button"
                        onClick={handleAdicionarTemplateMultiParte}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-schmersal-gray-dark hover:bg-gray-50"
                      >
                        {t('apreciacoes.editar.templates.adicionarCompleto')}
                      </button>
                    </div>
                  </div>
                  <TabelaPartes
                    riscos={riscos}
                    onAdicionar={handleAdicionarParte}
                    onAdicionarComJson={handleAbrirImportacaoJson}
                    onEditar={(parte, riscosParte) => {
                      // Agrupar riscos por parte para obter o número
                      const partesMap = new Map<string, { numero: number; parte: string; riscos: any[] }>();
                      riscos.forEach((risco) => {
                        const parteNome = risco.parte || 'Sem parte definida';
                        if (!partesMap.has(parteNome)) {
                          partesMap.set(parteNome, {
                            numero: partesMap.size + 1,
                            parte: parteNome,
                            riscos: []
                          });
                        }
                        partesMap.get(parteNome)!.riscos.push(risco);
                      });

                      const dadosParte = partesMap.get(parte);
                      if (dadosParte) {
                        setPaginaUltimaParte(14 + (dadosParte.numero - 1) * 3);
                        handleEditarParte(parte, riscosParte, dadosParte.numero);
                      }
                    }}
                    onDeletar={handleDeletarParte}
                    onDuplicar={handleDuplicarParte}
                    onReordenar={handleReordenarPartes}
                    onCriarTemplate={handleCriarTemplate}
                    onAdicionarTemplateParte={handleAdicionarTemplateMultiParte}
                    modoEdicao={true}
                  />
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Divisor redimensionável - apenas em desktop */}
        <div className="hidden lg:block flex-shrink-0">
          <ResizableDivider
            onResize={setLeftPanelWidth}
            initialLeftWidth={50}
          />
        </div>

        {/* Preview / Fotos do campo à direita */}
        <div
          className={`painel-redimensionavel flex-1 flex flex-col overflow-hidden ${activeTab === 'form' ? 'hidden lg:flex' : ''}`}
          style={{ ['--painel-largura' as string]: `${100 - leftPanelWidth}%` } as CSSProperties}
        >
          <div className="flex-shrink-0 flex border-b border-schmersal-gray-light dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker">
            <button
              type="button"
              onClick={() => setPainelDireito('preview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                painelDireito === 'preview'
                  ? 'border-schmersal-blue text-schmersal-blue'
                  : 'border-transparent text-schmersal-gray-medium'
              }`}
            >
              {t('apreciacoes.editar.painel.preview')}
            </button>
            {fotosVinculadas.length > 0 && (
              <button
                type="button"
                onClick={() => setPainelDireito('fotos')}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  painelDireito === 'fotos'
                    ? 'border-schmersal-blue text-schmersal-blue'
                    : 'border-transparent text-schmersal-gray-medium'
                }`}
              >
                {t('apreciacoes.editar.painel.fotosCampo', { total: fotosVinculadas.length })}
              </button>
            )}
          </div>

          {painelDireito === 'fotos' && fotosVinculadas.length > 0 ? (
            <div className="flex-1 overflow-y-auto p-3">
              <button
                type="button"
                onClick={handleEncaixarFotos}
                disabled={encaixando}
                className="w-full mb-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60"
                title={t('apreciacoes.editar.encaixe.tituloBotao')}
              >
                {encaixando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {encaixando
                  ? t('apreciacoes.editar.encaixe.analisando')
                  : encaixeResultado
                  ? t('apreciacoes.editar.encaixe.reprocessar')
                  : t('apreciacoes.editar.encaixe.encaixar')}
              </button>
              {encaixeResultado && !encaixando && (
                <button
                  type="button"
                  onClick={() => setMostrarEncaixe(true)}
                  className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-sm font-medium hover:bg-purple-50 dark:hover:bg-purple-950/30"
                  title={t('apreciacoes.editar.encaixe.tituloUltima')}
                >
                  <Sparkles className="h-4 w-4" /> {t('apreciacoes.editar.encaixe.verUltima')}
                  {encaixeGeradoEm && (
                    <span className="text-xs opacity-70">
                      ({formatarDataHora(encaixeGeradoEm, {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })})
                    </span>
                  )}
                </button>
              )}
              {encaixeErro && (
                <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-xs">{encaixeErro}</div>
              )}
              <p className="text-xs text-schmersal-gray-medium mb-3">
                {(() => {
                  const [antes, depois] = t('apreciacoes.editar.galeria.dica').split('{{acao}}');
                  return (
                    <>
                      {antes}
                      <strong>{t('comum.acao.copiar')}</strong>
                      {depois}
                    </>
                  );
                })()}
              </p>
              <FotosCampoGaleria fotos={fotosVinculadas} usadasUrls={fotosUsadasUrls} />
            </div>
          ) : (
            <PreviewLatex
              apreciacao={apreciacao}
              dados={dados}
              riscos={riscos}
              paginaUltimaParte={paginaUltimaParte}
            />
          )}
        </div>
      </div>

      {/* Modal de Edição de Parte */}
      {modalParteAberto && parteSelecionada && (
        <ModalEditarParte
          aberto={modalParteAberto}
          parte={parteSelecionada.parte}
          riscos={parteSelecionada.riscos}
          numeroParte={parteSelecionada.numero}
          onFechar={handleFecharModal}
          onSalvar={handleSalvarParte}
          descricaoMaquina={dados?.maquina?.descricao || ''}
          apreciacaoId={id}
          fotosCampo={fotosVinculadas}
          fotosCampoUsadasUrls={fotosUsadasUrls}
          pictogramas={pictogramas}
        />
      )}

      {/* Modal de resultados do "Encaixar fotos com IA" */}
      {encaixeResultado && mostrarEncaixe && (
        <div className="fixed inset-0 z-[65] bg-black/60 p-4 flex items-center justify-center">
          <div className="bg-white dark:bg-schmersal-gray-darker w-full max-w-4xl max-h-[90vh] rounded-lg overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2 text-schmersal-gray-dark dark:text-white">
                  <Sparkles className="h-5 w-5 text-purple-600" /> {t('apreciacoes.editar.encaixe.modalTitulo')}
                </h3>
                <p className="text-xs text-schmersal-gray-medium">
                  {t('apreciacoes.editar.encaixe.modalSubtitulo', {
                    total: encaixeResultado.totalFotos,
                  })}
                  {encaixeGeradoEm &&
                    t('apreciacoes.editar.encaixe.geradaEm', {
                      data: formatarDataHora(encaixeGeradoEm, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }),
                    })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMostrarEncaixe(false)}
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-schmersal-gray-dark"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {encaixeErro && (
              <div className="mx-4 mt-3 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{encaixeErro}</div>
            )}

            <div className="p-4 overflow-y-auto space-y-4">
              {encaixeResultado.partes.map((p) => {
                const temConteudo =
                  p.fotos.length > 0 ||
                  p.sugestoes.descricaoCondicaoAtual ||
                  p.sugestoes.perigosEncontrados ||
                  p.sugestoes.consequencias ||
                  p.sugestoes.eventoPerigoso;
                return (
                  <div key={p.parte} className="border border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold text-schmersal-gray-dark dark:text-white">{p.parte}</p>
                      <button
                        type="button"
                        onClick={() => aplicarEncaixeParte(p)}
                        disabled={!temConteudo || aplicandoParte === p.parte}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-schmersal-blue text-white text-sm disabled:opacity-50"
                      >
                        {aplicandoParte === p.parte ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        {t('apreciacoes.editar.encaixe.aplicarParte')}
                      </button>
                    </div>

                    {p.fotos.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {p.fotos.map((f) => (
                          <img
                            key={f.url}
                            src={withPreviewParams(toAbsoluteFotoUrl(f.url), 200, 55)}
                            alt={f.rotulo}
                            title={f.rotulo}
                            className="w-24 h-20 object-cover rounded border"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs italic text-schmersal-gray-medium">{t('apreciacoes.editar.encaixe.semFotos')}</p>
                    )}

                    <div className="mt-3 space-y-1.5 text-xs">
                      {p.sugestoes.descricaoCondicaoAtual && (
                        <p><span className="font-semibold">{t('apreciacoes.editar.encaixe.condicaoAtual')}</span> {p.sugestoes.descricaoCondicaoAtual}</p>
                      )}
                      {p.sugestoes.perigosEncontrados && (
                        <p><span className="font-semibold">{t('apreciacoes.editar.encaixe.perigos')}</span> {p.sugestoes.perigosEncontrados}</p>
                      )}
                      {p.sugestoes.consequencias && (
                        <p><span className="font-semibold">{t('apreciacoes.editar.encaixe.consequencias')}</span> {p.sugestoes.consequencias}</p>
                      )}
                      {p.sugestoes.eventoPerigoso && (
                        <p><span className="font-semibold">{t('apreciacoes.editar.encaixe.eventoPerigoso')}</span> {p.sugestoes.eventoPerigoso}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 border-t flex justify-end">
              <button
                type="button"
                onClick={() => setMostrarEncaixe(false)}
                className="px-4 py-2 rounded border text-sm text-schmersal-gray-dark dark:text-white"
              >
                {t('comum.acao.fechar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Importação JSON de Parte (Ctrl/Cmd + clique em "Adicionar Parte") */}
      {modalImportacaoJsonAberto && (
        <div className="fixed inset-0 z-50 bg-black/60 p-4 flex items-center justify-center">
          <div className="w-full max-w-3xl bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-xl border border-schmersal-gray-light dark:border-schmersal-gray-dark">
            <div className="p-5 border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
              <h3 className="text-lg font-bold text-schmersal-blue dark:text-white">
                {t('apreciacoes.editar.json.titulo')}
              </h3>
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mt-1">
                {t('apreciacoes.editar.json.subtitulo')}
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                  {t('apreciacoes.editar.json.nomeParte')}
                </label>
                <input
                  type="text"
                  value={jsonParteNome}
                  onChange={(e) => setJsonParteNome(e.target.value)}
                  placeholder={t('apreciacoes.editar.json.nomePartePlaceholder')}
                  className="w-full border border-rg-line dark:border-schmersal-gray-dark rounded-lg px-3 py-2
                           bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { indice: 1, file: jsonFotoParte1, setFile: setJsonFotoParte1 },
                  { indice: 2, file: jsonFotoParte2, setFile: setJsonFotoParte2 },
                  { indice: 3, file: jsonFotoParte3, setFile: setJsonFotoParte3 },
                  { indice: 4, file: jsonFotoParte4, setFile: setJsonFotoParte4 },
                ].map((slot) => (
                  <div
                    key={slot.indice}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file) slot.setFile(file);
                    }}
                    className="border-2 border-dashed border-schmersal-gray-light dark:border-schmersal-gray-dark rounded-lg p-3 bg-white dark:bg-schmersal-gray-darkest"
                  >
                    <label className="block text-xs font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                      {t('apreciacoes.editar.json.imagemParte', { indice: slot.indice })}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        slot.setFile(file);
                      }}
                      className="block w-full text-xs text-schmersal-gray-dark dark:text-white"
                    />
                    <p className="mt-2 text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light truncate">
                      {slot.file
                        ? t('apreciacoes.editar.json.selecionado', { arquivo: slot.file.name })
                        : t('apreciacoes.editar.json.arraste')}
                    </p>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">
                  {t('apreciacoes.editar.json.rotuloJson')}
                </label>
                <textarea
                  value={jsonImportacao}
                  onChange={(e) => setJsonImportacao(e.target.value)}
                  rows={14}
                  placeholder={t('apreciacoes.editar.json.exemploPlaceholder')}
                  className="w-full font-mono text-xs border border-rg-line dark:border-schmersal-gray-dark rounded-lg px-3 py-2
                           bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white"
                />
              </div>
            </div>

            <div className="p-5 border-t border-schmersal-gray-light dark:border-schmersal-gray-dark flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleFecharImportacaoJson}
                disabled={importandoJson}
                className="px-4 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                         text-schmersal-gray-dark dark:text-white disabled:opacity-60"
              >
                {t('comum.acao.cancelar')}
              </button>
              <button
                type="button"
                onClick={handleImportarParteViaJson}
                disabled={importandoJson}
                className="px-4 py-2 rounded-lg bg-schmersal-blue text-white font-semibold disabled:opacity-60"
              >
                {importandoJson ? t('apreciacoes.editar.json.importando') : t('apreciacoes.editar.json.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Template de Parte */}
      {modalCriarTemplateAberto && parteParaTemplate && id && (
        <ModalCriarTemplateParte
          aberto={modalCriarTemplateAberto}
          onFechar={handleFecharModalCriarTemplate}
          apreciacaoId={id}
          parteNome={parteParaTemplate.parte}
          projetoId={apreciacao?.projetoId}
          onSucesso={handleSucessoCriarTemplate}
        />
      )}

      {/* Modal Selecionar Template de Parte */}
      {modalSelecionarTemplateAberto && id && (
        <ModalSelecionarTemplateParte
          aberto={modalSelecionarTemplateAberto}
          onFechar={handleFecharModalSelecionarTemplate}
          apreciacaoId={id}
          projetoId={apreciacao?.projetoId}
          onSucesso={handleSucessoAplicarTemplate}
        />
      )}

      {/* Modal Criar Template Multiparte */}
      {modalCriarTemplateMultiParteAberto && id && (
        <ModalCriarTemplateMultiParte
          aberto={modalCriarTemplateMultiParteAberto}
          onFechar={handleFecharModalCriarTemplateMultiParte}
          apreciacaoId={id}
          projetoId={apreciacao?.projetoId}
          onSucesso={handleSucessoCriarTemplateMultiParte}
        />
      )}

      {/* Modal Selecionar Template Multiparte */}
      {modalSelecionarTemplateMultiParteAberto && id && (
        <ModalSelecionarTemplateMultiParte
          aberto={modalSelecionarTemplateMultiParteAberto}
          onFechar={handleFecharModalSelecionarTemplateMultiParte}
          apreciacaoId={id}
          projetoId={apreciacao?.projetoId}
          onSucesso={handleSucessoAplicarTemplateMultiParte}
        />
      )}
    </div>
  );
}
